import React, { useState, useEffect, useMemo, useCallback } from "react";
import { ToastContainer } from "react-toastify";
import { Button } from "react-bootstrap";
import PageTitle from "../../layouts/PageTitle";
import Select from "react-select";
import Table from "../../components/table/ReactTable/Table";
import { ColumnFilter } from "../../components/table/ReactTable/ColumnFilter";
import { useSelector, useDispatch } from "react-redux";
import useFullPageLoader from "../../../hooks/useFullPageLoader";
import { inventoryHistoryActions } from "../../../_store";
import MRPurchaseOrderPDF from "../../components/export/MRPurchaseOrderPDF";
import transactionReportXslx from "../../components/export/transactionReportXlsx";

const MRPurchaseOrderReport = () => {
  const dispatch = useDispatch();
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  // #region Redux Data
  const mrForDropdown = useSelector(
    (x) => x.inventoryHistory.merchandiseReceptionForDropdown
  );

  const MRPurchaseOrderListReport = useSelector(
    (x) => x.inventoryHistory.MRPurchaseOrderListReport
  );
  // #endregion

  const [finalTableData, setFinalTableData] = useState([]);

  const handleTableDataUpdate = useCallback((data) => {
    setFinalTableData(data);
  }, []);

  useEffect(() => {
    dispatch(
      inventoryHistoryActions.GetMerchandiseReceptionForDropdown({
        MerchandiseReceptionID: 0,
        IsCompleted: true,
        Status: 1,
      })
    );

    dispatch(inventoryHistoryActions.clearMRPurchaseOrderReport());
  }, [dispatch]);

  // ---------------------------------------------------------------------
  // TABLE COLUMNS BASED ON REQUIRED SQL FIELDS
  // ---------------------------------------------------------------------
  const COLUMNS = [
    {
      Header: "Product Name",
      Footer: "Product Name",
      accessor: "ProductName",
      justify: "center",
      Filter: ColumnFilter,
    },
    {
      Header: "SKU",
      Footer: "SKU",
      accessor: "SKU",
      justify: "center",
      Filter: ColumnFilter,
    },
    {
      Header: "Order Units",
      Footer: "Order Units",
      accessor: "OrderUnits",
      justify: "center",
      Filter: ColumnFilter,
    },
    {
      Header: "Processed Units",
      Footer: "Processed Units",
      accessor: "OrderProcessedUnits",
      justify: "center",
      Filter: ColumnFilter,
    },
    {
      Header: "Lot Number",
      Footer: "Lot Number",
      accessor: "LotNumber",
      justify: "center",
      Filter: ColumnFilter,
    },
    {
      Header: "Lot Date",
      Footer: "Lot Date",
      accessor: "LotDateString",
      justify: "center",
      Filter: ColumnFilter,
      fieldType: "Date",
    },
    {
      Header: "Units Per Box",
      Footer: "Units Per Box",
      accessor: "UnitsPerBox",
      justify: "center",
      Filter: ColumnFilter,
    },
  ];

  // ---------------------------------------------------------------------
  // EXPORT → PDF COLUMNS
  // ---------------------------------------------------------------------
  const generateColumns = [
    { header: "Product Name", dataKey: "ProductName" },
    { header: "SKU", dataKey: "SKU" },
    { header: "Order Units", dataKey: "OrderUnits" },
    { header: "Processed Units", dataKey: "OrderProcessedUnits" },
    { header: "Lot Number", dataKey: "LotNumber" },
    { header: "Lot Date", dataKey: "LotDateString" },
    { header: "Units Per Box", dataKey: "UnitsPerBox" },
  ];

  const columnStyles = {
    ProductName: { halign: "center", cellWidth: 45 },
    SKU: { halign: "center", cellWidth: 30 },
    OrderUnits: { halign: "center", cellWidth: 30 },
    OrderProcessedUnits: { halign: "center", cellWidth: 35 },
    LotNumber: { halign: "center", cellWidth: 30 },
    LotDateString: { halign: "center", cellWidth: 30 },
    UnitsPerBox: { halign: "center", cellWidth: 30 },
  };

  // ---------------------------------------------------------------------
  // EXPORT → EXCEL COLUMNS
  // ---------------------------------------------------------------------
  const generateExcelColumns = [
    "Product Name",
    "SKU",
    "Order Units",
    "Processed Units",
    "Lot Number",
    "Lot Date",
    "Units Per Box",
  ];

  const new_exportList =
    finalTableData &&
    finalTableData.map((obj) => ({
      ProductName: obj.ProductName,
      SKU: obj.SKU,
      OrderUnits: obj.OrderUnits,
      OrderProcessedUnits: obj.OrderProcessedUnits,
      LotNumber: obj.LotNumber,
      LotDateString: obj.LotDateString,
      UnitsPerBox: obj.UnitsPerBox,
    }));

  const handleClickPDF = () => {
    let title = "Purchase Order Report - Product Details";
    MRPurchaseOrderListReport?.merchandiseReceptionModel &&
      MRPurchaseOrderPDF(
        finalTableData,
        generateColumns,
        true,
        title,
        MRPurchaseOrderListReport?.merchandiseReceptionModel,
        columnStyles
      );
  };

  const headerData = {
    "Purchase Order #":
      MRPurchaseOrderListReport?.merchandiseReceptionModel?.PurchaseOrderNumber,
    "Invoice Number":
      MRPurchaseOrderListReport?.merchandiseReceptionModel?.InvoiceNumber,
    "Vendor Name": MRPurchaseOrderListReport?.merchandiseReceptionModel?.Vendor,
    "Warehouse #":
      MRPurchaseOrderListReport?.merchandiseReceptionModel?.WareHouseNumber,
  };

  const handleClickExcel = () => {
    let title = "Purchase Order Report - Product Details";
    transactionReportXslx(
      new_exportList,
      headerData,
      generateExcelColumns,
      title
    );
  };

  const handleProductChange = (e) => {
    if (!e?.value) return;
    dispatch(inventoryHistoryActions.getMRPurchaseOrderReport(e.value));
  };

  const columns = useMemo(() => COLUMNS, []);

  return (
    <>
      <PageTitle
        activeMenu="Purchase Order Report"
        motherMenu="Inventory
"
      />
      <ToastContainer />

      <div className="card">
        <div className="card-header">
          <h4 className="card-title header-title">Purchase Order Report</h4>

          <div className="float-sm-end">
            <Button
              className="me-2"
              variant="danger btn-rounded"
              onClick={handleClickPDF}
            >
              <span className="btn-icon-start text-danger">
                <i className="fa fa-solid fa-file-pdf"></i>
              </span>
              PDF
            </Button>

            <Button
              className="me-2"
              variant="secondary btn-rounded"
              onClick={handleClickExcel}
            >
              <span className="btn-icon-start text-secondary">
                <i className="fa fa-regular fa-file-excel"></i>
              </span>
              Excel
            </Button>
          </div>
        </div>

        {/* DROPDOWN */}
        <div className="card-body">
          <div className="row">
            <div className="col-md-3 mb-4">
              <label className="text-black font-w500 filtercss">
                Purchase Order #
              </label>
              <Select
                classNamePrefix="custom_select_css"
                name="ExistingPurchaseOrderID"
                onChange={handleProductChange}
                options={mrForDropdown}
              />
            </div>
          </div>

          {/* HEADER BLOCK (KEPT AS YOU SELECTED - OPTION B) */}
          {MRPurchaseOrderListReport?.merchandiseReceptionModel
            ?.MerchandiseReceptionID ? (
            <div className="row">
              <div
                className="col-12 d-flex ml-4 gap-2 mr-4 p-4 border shadow-lg rounded-lg"
                style={{ backgroundColor: "#f5f3f3" }}
              >
                <div className="col-6 d-flex flex-column gap-4">
                  <div className="d-flex gap-2 col-12">
                    <div className="col-3 font-w500">Purchase Order #:</div>
                    <div className="col-9">
                      {
                        MRPurchaseOrderListReport?.merchandiseReceptionModel
                          .PurchaseOrderNumber
                      }
                    </div>
                  </div>

                  <div className="d-flex gap-2 col-12">
                    <div className="col-3 font-w500">Invoice Number:</div>
                    <div className="col-9">
                      {
                        MRPurchaseOrderListReport?.merchandiseReceptionModel
                          .InvoiceNumber
                      }
                    </div>
                  </div>
                </div>

                <div className="col-6 d-flex flex-column gap-4">
                  <div className="d-flex gap-2 col-12">
                    <div className="col-3 font-w500">Vendor Name:</div>
                    <div className="col-9">
                      {
                        MRPurchaseOrderListReport?.merchandiseReceptionModel
                          .Vendor
                      }
                    </div>
                  </div>

                  <div className="d-flex gap-2 col-12">
                    <div className="col-3 font-w500">Warehouse #:</div>
                    <div className="col-9">
                      {
                        MRPurchaseOrderListReport?.merchandiseReceptionModel
                          .WareHouseNumber
                      }
                    </div>
                  </div>
                </div>
              </div>

              {/* TABLE */}
              <div className="mt-5">
                <Table
                  columns={columns}
                  data={
                    MRPurchaseOrderListReport?.merchandiseReceptionProductModel ||
                    []
                  }
                  onDataUpdate={handleTableDataUpdate}
                />
              </div>
            </div>
          ) : (
            <div className="row d-flex justify-content-center fs-3">
              Select Purchase Order Number to get a Purchase Order Report
            </div>
          )}
        </div>
      </div>

      {loader}
    </>
  );
};

export default MRPurchaseOrderReport;
